import * as React from 'react'
import '../settings.css'
import {
  LimitType,
  Model,
  MutedClientMetricsDict,
  Permission,
  User,
} from '../types/types'
import { assertNever, defaultMetricMutes, getRCForLimitType } from '../utils'
import {ClientCode, ClientConfig} from "../types/clients";
import {getActiveClients} from "../web/clients";
import {useEffect, useState} from "react";
import { useDispatch, useSelector } from 'react-redux';
import { actionCreators } from '../actions';

export interface Props {
  readonly mutedClientMetrics: MutedClientMetricsDict
  readonly onLogoutClicked: () => void
  readonly updateTCSLimitsClicked: () => void
  readonly toggleMute: (clientCode: ClientCode, metric: LimitType) => () => void
  readonly user: User
  readonly hasPermission: {
    (permission: Permission): (user: User) => boolean
    (permission: Permission, user: User): boolean
  }
}

function LogoutButton(
  user: Props['user'],
  onLogoutClicked: Props['onLogoutClicked'],
): JSX.Element {
  switch (user.type) {
    case 'LoggedIn':
    case 'NotLoggedIn':
    case 'FailedToLogIn':
    case 'LoggingIn':
      return (
        <div>
          <button className='button' type='button' onClick={onLogoutClicked}>
            Logout
          </button>
        </div>
      )
    case 'FailedToLogout':
      return (
        <div>
          <div>Logout failed please try again.</div>
          <button className='button' type='button' onClick={onLogoutClicked}>
            Logout
          </button>
        </div>
      )
    default:
      return assertNever(user)
  }
}

function TCSLimitUpdateButton(
  updateTCSLimitsClicked: Props['updateTCSLimitsClicked'],
): JSX.Element {
  return (
    <>
      <h2>TCS Limit Management</h2>
      <div>
        <button
          className='button'
          type='button'
          onClick={updateTCSLimitsClicked}
        >
          Update All Client TCS Limits
        </button>
      </div>
    </>
  )
}

/**
 * Mutable as in can-be-muted, not can-be-mutated.
 */
const mutableMetrics: ReadonlyArray<LimitType> = [
  'RC2_exposure',
  'RC6_unactioned_orders',
  'Virtual_server_suspension'
]

/**
 *
 * TODO: indicate the current level per client+metric, to make it easier to see which to mute.
 */
function alertMuting(
  clients: ReadonlyArray<ClientConfig>,
  mutedClientMetrics: MutedClientMetricsDict,
  toggleMute: Props['toggleMute'],
): JSX.Element {
  function alertMutingPerClient(client: ClientConfig): JSX.Element {
    const mutedMetrics = mutedClientMetrics[client.code] || defaultMetricMutes

    function muteButtonsForMetrics(metric: LimitType): JSX.Element {
      const isMuted = mutedMetrics[metric] === 'muted'
      return (
        <button
          key={client.name + ' ' + metric}
          onClick={toggleMute(client.code, metric)}
          className={`button narrow-margins mute-control mute-control--${
            mutedMetrics[metric]
          }`}
        >
          {isMuted ? 'Un-mute' : 'mute'} {getRCForLimitType(metric)}
        </button>
      )
    }

    return (
      <tr key={client.name}>
        <th>{client.name}</th>
        <td>{mutableMetrics.map(muteButtonsForMetrics)}</td>
      </tr>
    )
  }
  return (
    <div>
      <h2>Click to mute/unmute alerts.</h2>
      <table>
        <tbody>{clients.map(alertMutingPerClient)}</tbody>
      </table>
    </div>
  )
}

function Settings({
  mutedClientMetrics,
  onLogoutClicked,
  toggleMute,
  updateTCSLimitsClicked,
  user,
  hasPermission
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const loadClients = async () => {
    const clients = await getActiveClients();
    dispatch(actionCreators.setClientSettings(clients));
  }
  const clientConfigs = useSelector<Model, ReadonlyArray<ClientConfig>>(state => state.clientSettings)
  useEffect(() => {
    loadClients();
  }, [])

  return (
    <>
      {LogoutButton(user, onLogoutClicked)}
      {hasPermission('canViewClientDetails', user) && <>
        <hr />
        {TCSLimitUpdateButton(updateTCSLimitsClicked)}
        <hr />
        {alertMuting(clientConfigs, mutedClientMetrics, toggleMute)}
      </>}
    </>
  )
}

export default Settings
